import { useEffect, useState } from 'react'
import { type ChildMemberDetails } from 'Pages/CaregiverPortal/Members/ChildDisplay'
import { type ChildRelationship } from 'Pages/CaregiverPortal/Members/ChildRelationships'
import { ChildServices } from 'Pages/CaregiverPortal/Members/ChildServices'
import { sendGet } from 'hooks/use-fetch'
import { ChildProfileCard } from 'Components/Cards/CaregiverPortal/ChildProfileCard'
import { AgeFromDate } from 'core/Utilities'
import { Button, CircularProgress, Typography } from '@mui/material'
import { type lookup } from 'core/types'
import { CAREGIVER_PORTAL_MEMBERS_EDIT_CHILD, CAREGIVER_PORTAL_SERVICE_FINDER } from 'Pages/Routes'
import { generatePath, useNavigate } from 'react-router'

export interface ChildDetailsProps {
  child: ChildMemberDetails
}

export function ChildDetails (props: ChildDetailsProps): JSX.Element {
  const nav = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [captureIEP, setCaptureIEP] = useState(false)
  const [captureIFSP, setCaptureIFSP] = useState(false)
  const [captureEOIS, setCaptureEOIS] = useState(false)
  const [raceItems, setRaceItems] = useState<lookup[]>([])
  const [relationships, setRelationships] = useState<ChildRelationship[]>([])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const [isdResult, raceResult, relationshipResult] = await Promise.all([
        sendGet(`/CaregiverPortal/GetISDSettings?id=${props.child.isdid ?? ''}`, {}),
        sendGet('/Race/GetLookup', {}),
        sendGet(`/CaregiverPortal/GetChildRelationships/${props.child.id}`, {})
      ])

      if (isdResult.response != null) {
        setCaptureIEP(isdResult.response.captureIEP)
        setCaptureIFSP(isdResult.response.captureIFSP)
        setCaptureEOIS(isdResult.response.captureEOIS)
      }
      setRaceItems(raceResult.response)
      if (relationshipResult.success) {
        setRelationships(relationshipResult.response)
      }
      setIsLoading(false)
    }
    void fetchData()
  }, [props.child.dateOfBirth])

  function editChild (): void {
    const route = generatePath(CAREGIVER_PORTAL_MEMBERS_EDIT_CHILD, { childId: props.child.id.toString() })
    nav(route)
  }

  function addService (): void {
    nav(CAREGIVER_PORTAL_SERVICE_FINDER)
  }

  function raceDisplay (): string {
    let result = ''
    props.child.races.forEach((raceID) => {
      if (result !== '') {
        result += ', '
      }
      result += raceItems.find(r => r.id === raceID)?.name ?? ''
    })
    return result
  }

  if (isLoading) {
    return <CircularProgress />
  }

  // taking child's age into consideration for when to show IEP/IFSP/EOIS, matching prior behavior
  const showIEP = captureIEP && props.child.dateOfBirth !== null && AgeFromDate(props.child.dateOfBirth) >= 3
  const showIFSP = captureIFSP && props.child.dateOfBirth !== null && AgeFromDate(props.child.dateOfBirth) < 3
  const showEOIS = captureEOIS && props.child.dateOfBirth !== null && AgeFromDate(props.child.dateOfBirth) >= 3

  return <>
    <ChildProfileCard
      child={props.child}
      raceDisplay={raceDisplay()}
      showIEP={showIEP}
      showIFSP={showIFSP}
      showEOIS={showEOIS}
      relationships={relationships}
    />

    {props.child.canEdit &&
      <Button
        variant='contained'
        className='footer-button'
        name='editDetails'
        onClick={editChild}
      >
        Edit Details
      </Button>
    }

    <Typography variant='h6' sx={{ fontWeight: 'bold', mb: '24px' }}>Active Services</Typography>
    <ChildServices childID={props.child.id} canEdit={props.child.canEdit} />

    {props.child.canEdit &&
      <Button
        variant='contained'
        className='footer-button'
        sx={{ mt: '20px' }}
        name='addService'
        onClick={addService}
      >
        Add Service
      </Button>
    }
  </>
}
